import React, { useState, useRef } from 'react';
import {
  TextField,
  Button,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  SelectChangeEvent,
  Autocomplete,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  DialogActions,
  Box,
  IconButton,
  Alert,
  CircularProgress,
  Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HomeIcon from '@mui/icons-material/Home';
import ReplayIcon from '@mui/icons-material/Replay';
import { MuiOtpInput } from 'mui-one-time-password-input';
import KeywordSearchDropdown from './KeywordSearchDropdown';
import IRequestFormData from '../models/IRequestFormData';
import { IKeyword } from '../models/IKeyword';
import userTypesData from '../assets/AthenaUserTypes2.json';
import NPSDegreeData from '../assets/NPSDegrees.json';
import AthenaServiceTypes from '../assets/AthenaServiceTypes.json';
import NPSDepartments from '../assets/NPSDepartments.json';
import AthenaRankTypes from '../assets/AthenaRankTypes.json';
import { submitFormData, sendOTP, verifyOTP } from '../api/athena4p-api';
import npsfLogoWhite from '../assets/npsf-logo-white.png';
import IRequestSubmissionData from '../models/IRequestSubmissionData';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/material.css';
import { CountryData } from 'react-phone-input-2';
import INPSDegrees from '../models/INPSDegrees';
import INPSDepartments from '../models/INPSDepartments';
import IAthenaRankTypes from '../models/IAthenaRankTypes';
import IAthenaServiceTypes from '../models/IAthenaServiceTypes';
// import { IAthenaUniversity } from '../models/IAthenaUniversity';
// import { IAthenaNation } from '../models/IAthenaNation';
import AthenaUniversities from '../assets/AthenaUniversities.json';
import AthenaNations from '../assets/AthenaNations.json';
import IAddUsers from '../models/IAddUsers';
import '../styles/RequestForm.css';

type Severity = 'error' | 'success' | 'info' | 'warning' | undefined;

const RequestForm: React.FC = () => {
  const [formData, setFormData] = useState<IRequestFormData>({
    firstName: '',
    middleName: '',
    lastName: '',
    maidenName: '',
    email: '',
    phoneNumber: '',
    password: '',
    userType: '',
    isAlumni: false,
    nodeTypeId: 0,
    researchInterests: [],
    achieveGoal: '',
    sendCopy: false,
    department: '',
    organization: '',
    rank: '',
    course: '',
    otherService: '',
    nationality: '',
    universityCollege: [],
    DepartmentId: 0,
    GraduateId: 0,
  });
  const [additionalFields, setAdditionalFields] = useState<any[]>([]);
  const [passwordError, setPasswordError] = useState<boolean>(false);
  const [formSubmitted, setFormSubmitted] = useState<boolean>(false);
  const [isValid, setIsValid] = useState<boolean>(false);
  const [degreeOptions, setDegreeOptions] = useState<string[]>([]);
  const [selectedSchools, setSelectedSchools] = useState<string[]>([]);
  const [serviceOptions, setServiceOptions] = useState<string[]>([]);
  const [rankOptions, setRankOptions] = useState<string[]>([]);
  const [departmentOptions, setDepartmentOptions] = useState<string[]>([]);
  const [selectedChips, setSelectedChips] = useState<string[]>([]);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [otp, setOtp] = useState('');
  const [otpStatusMessage, setOtpStatusMessage] = useState<string>('');
  const [formSubmissionData, setFormSubmissionData] =
    useState<IRequestSubmissionData>({} as IRequestSubmissionData);
  const [otpVerified, setOtpVerified] = useState<boolean>(false);
  const [alertSeverity, setAlertSeverity] = useState<Severity>('info');
  const [userSubmitted, setUserSubmitted] = useState<boolean>(false);
  const [addingUser, setAddingUser] = useState<boolean>(true);
  const [firstName, setFirstName] = useState<string>('');
  const [isAlumni, setIsAlumni] = useState<boolean | null>(null);

  const passwordRef = useRef<any>(null);
  const phoneNumberRef = useRef<any>(null);
  const gradSchoolsRef = useRef<any>(null);

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSelectChange = (value: string, fieldTitle: string) => {
    const name =
      fieldTitle && fieldTitle.includes('/')
        ? replaceSlashes(fieldTitle)
        : replaceSpaces(fieldTitle);
    setFormData({ ...formData, [name]: value });

    if (fieldTitle === 'Organization/Service' && value === 'Other') {
      setFormData({ ...formData, otherService: '' });
    } else if (fieldTitle === 'Organization/Service') {
      setFormData({ ...formData, organization: value });
    } else if (fieldTitle === 'Rank') {
      setFormData({ ...formData, rank: value });
    }
  };

  const handleServiceChange = (value: string) => {
    const selectedService = AthenaServiceTypes.find(
      (service: IAthenaServiceTypes) => service.Title === value
    );

    const useServiceId = selectedService ? selectedService.UseServiceId : 0;

    if (useServiceId === 0) {
      setRankOptions([]);
    } else {
      const rankOptions = AthenaRankTypes.filter(
        (rank: IAthenaRankTypes) => rank.ServiceTypeId === useServiceId
      )
        .sort((a, b) => a.SortOrder - b.SortOrder)
        .map((rank: IAthenaRankTypes) => rank.RankTitle);
      setRankOptions(rankOptions);
    }

    setFormData({ ...formData, organization: value });
  };

  const handleDropdownChange = (event: SelectChangeEvent<string>) => {
    const userType = event.target.value;
    setFormData({ ...formData, userType });

    const selectedUserType = userTypesData.find(
      (type: any) => type.Title === userType
    );
    const userTypeId = selectedUserType ? selectedUserType.UserTypeId : 0;

    const fields = userTypesData.filter(
      (type: any) => type.ParentId === userTypeId
    );
    setAdditionalFields(fields);

    if (fields.some((field) => field.Title === 'Degree(s)')) {
      const degreeOptions = NPSDegreeData.map(
        (degree: INPSDegrees) => degree.GraduateProgramTypeLongName
      ).sort((a, b) => a.localeCompare(b));
      setDegreeOptions(degreeOptions);
    } else {
      setDegreeOptions([]);
    }

    if (
      fields.some(
        (field) =>
          field.Title === 'Organization/Service' ||
          field.Title === 'Organization' ||
          field.Title === 'Current Organization/Service'
      )
    ) {
      const serviceOptions = AthenaServiceTypes.filter(
        (service: IAthenaServiceTypes) => service.DisplayToUser
      )
        .map((service: IAthenaServiceTypes) => service.Title)
        .sort((a, b) => a.localeCompare(b));
      setServiceOptions(serviceOptions);
    } else {
      setServiceOptions([]);
    }

    if (fields.some((field) => field.Title === 'Rank')) {
      const rankOptions = AthenaRankTypes.filter(
        (rank: IAthenaRankTypes) => rank.ServiceTypeId !== 0
      )
        .sort((a, b) => a.SortOrder - b.SortOrder)
        .map((rank: IAthenaRankTypes) => rank.RankTitle);
      setRankOptions(rankOptions);
    } else {
      setRankOptions([]);
    }

    if (
      fields.some(
        (field) =>
          field.Title === 'Department(s)' || field.Title === 'Department'
      )
    ) {
      const departmentOptions = NPSDepartments.map(
        (department: INPSDepartments) => department.Description
      ).sort((a, b) => a.localeCompare(b));
      setDepartmentOptions(departmentOptions);
    } else {
      setDepartmentOptions([]);
    }
  };

  const handlePhoneNumberChange = (value: string, data: CountryData) => {
    const dialCode = data.dialCode;
    if (!value && formSubmitted) {
      setIsValid(false);
      return;
    } else {
      if (dialCode) {
        const phn = value.slice(dialCode.length);
        if (!phn && formSubmitted) {
          setIsValid(false);
          return;
        }
      }
    }
    const phoneNumber = `+${value}`;
    setIsValid(true);
    setFormData({ ...formData, phoneNumber: phoneNumber });
  };

  const handleChipChange = (fieldTitle: string, selectedItems: string[]) => {
    setSelectedChips({ ...selectedChips, [fieldTitle]: selectedItems });
    const name = fieldTitle.includes('/')
      ? replaceSlashes(fieldTitle)
      : replaceSpaces(fieldTitle);
    setFormData({ ...formData, [name]: selectedItems.join('||') });
  };

  const handleGradSchoolsChange = (_event: any, selectedSchools: any) => {
    if (selectedSchools) {
      setSelectedSchools(selectedSchools);
      setFormData({ ...formData, universityCollege: selectedSchools });
    }
  };

  const getSelectedKeywords = (keywords: IKeyword[]) => {
    setFormData({ ...formData, researchInterests: keywords });
  };

  const replaceSpaces = (str: string) => {
    return str
      .toLowerCase()
      .replace(/(?:^|\s)(.)/g, (_, char) => char.toUpperCase())
      .replace(/\s+/g, '')
      .replace(/^./, (str) => str.toLowerCase());
  };

  const replaceSlashes = (str: string) => {
    return str
      .toLowerCase()
      .replace(/\/(.)/g, (_: any, char: any) => char.toUpperCase());
  };

  const renderAdditionalFields: any = () => {
    return additionalFields.map((field, index) => {
      const isRequired =
        field.ParentId === 0 ? true : field.Required !== undefined;

      if (
        field.Title === 'Most Recent Alma Mater' ||
        field.Title === 'University/College' ||
        field.Title === 'Graduate School(s)'
      ) {
        return (
          <Autocomplete
            onChange={handleGradSchoolsChange}
            clearIcon={false}
            options={AthenaUniversities.map((uni) => uni.Title)}
            freeSolo
            multiple
            renderTags={(value, props) =>
              value.map((option, index) => (
                <Chip label={option} {...props({ index })} />
              ))
            }
            renderInput={(params) => (
              <TextField
                ref={gradSchoolsRef}
                required={selectedSchools.length === 0}
                {...params}
                key={index}
                label={field.Title}
                helperText='Type the school name and press enter key to add'
              />
            )}
          />
        );
      } else if (field.Title === 'Degree(s)') {
        return (
          <Autocomplete
            onChange={(_event: any, value: any) =>
              handleChipChange(field.Title, value)
            }
            multiple
            disablePortal
            options={degreeOptions}
            freeSolo
            fullWidth
            renderInput={(params) => (
              <TextField
                {...params}
                label={field.Title}
                required={
                  !selectedChips[field.Title] ||
                  selectedChips[field.Title].length === 0
                }
                helperText='Type to search or add new degree...'
              />
            )}
          />
        );
      } else if (
        field.Title === 'Organization/Service' ||
        field.Title === 'Organization' ||
        field.Title === 'Current Organization/Service'
      ) {
        return (
          <>
            <Autocomplete
              onChange={(_event: any, value: any) => {
                handleServiceChange(value);
              }}
              disablePortal
              options={serviceOptions}
              freeSolo
              fullWidth
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={field.Title}
                  required={isRequired}
                  helperText='Type to search or add new service...'
                />
              )}
            />
            {formData.organization === 'Other' && (
              <TextField
                label='Please specify your service organization'
                name='otherService'
                fullWidth
                required={isRequired}
                margin='normal'
                variant='outlined'
                onChange={handleInputChange}
              />
            )}
          </>
        );
      } else if (field.Title === 'Rank') {
        return (
          <>
            {formData.organization && (
              <Autocomplete
                freeSolo
                onInputChange={(_event, value) => {
                  setFormData({ ...formData, rank: value }); // Capture custom input
                }}
                options={rankOptions} // Existing options
                fullWidth
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label='Rank'
                    required
                    helperText='Type to search or add new rank...'
                  />
                )}
              />
            )}
          </>
        );
      } else if (
        field.Title === 'Department(s)' ||
        field.Title === 'Department'
      ) {
        return (
          <Autocomplete
            onChange={(_event: any, value: any) =>
              handleChipChange(field.Title, value)
            }
            disablePortal
            options={departmentOptions}
            multiple
            freeSolo
            fullWidth
            renderInput={(params) => (
              <TextField
                {...params}
                label={field.Title}
                required={
                  !selectedChips[field.Title] ||
                  selectedChips[field.Title].length === 0
                }
                helperText='Type to search or add new department...'
              />
            )}
          />
        );
      } else if (
        field.Title === 'Start Date' ||
        field.Title === 'Expected Graduation Date' ||
        field.Title === 'Most Recent Graduation Date' ||
        field.Title === 'Graduation Date'
      ) {
        const name =
          field.Title && field.Title.includes('/')
            ? replaceSlashes(field.Title)
            : replaceSpaces(field.Title);
        return (
          <TextField
            key={index}
            label={field.Title}
            name={name}
            type='date'
            // type='number'
            // inputProps={{ min: 1900, max: new Date().getFullYear() }}
            fullWidth
            required={isRequired}
            margin='normal'
            variant='outlined'
            onChange={handleInputChange}
            InputLabelProps={{
              shrink: true,
            }}
            helperText={
              field.Title === 'Start Date'
                ? 'Please enter the year you started'
                : 'Please enter your expected graduation year'
            }
          />
        );
      } else if (field.Title === 'Nationality') {
        return (
          <Autocomplete
            onChange={(_event: any, value: any) => {
              handleSelectChange(value, field.Title);
            }}
            disablePortal
            options={AthenaNations.map((nation) => nation.Title)}
            freeSolo
            fullWidth
            renderInput={(params) => (
              <TextField
                {...params}
                label={field.Title}
                required={isRequired}
                helperText='Type to search or add new nationality...'
              />
            )}
          />
        );
      } else {
        return (
          <TextField
            key={index}
            label={field.Title}
            name={
              field.Title && field.Title.includes('/')
                ? replaceSlashes(field.Title)
                : replaceSpaces(field.Title)
            }
            fullWidth
            required={isRequired}
            margin='normal'
            variant='outlined'
            onChange={handleInputChange}
          />
        );
      }
    });
  };

  const formSubmission = async (e: any) => {
    if (e) {
      e.preventDefault();
    }
    setFormSubmitted(true);
    const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;
    const passwordError =
      formData.password && regex.test(formData.password) ? false : true;
    setPasswordError(passwordError);

    if (passwordError) {
      passwordRef.current && passwordRef.current.focus();
      return;
    }

    if (!isValid) {
      alert('Enter valid phone number');
      phoneNumberRef.current && phoneNumberRef.current.focus();
      return;
    }

    const userType = userTypesData.find((userType) => {
      if (userType.Title === formData.userType) {
        return userType;
      }
    });

    formData.nodeTypeId = userType ? userType.NodeTypeId : 0;

    let gradSchools: string = '';
    if (Array.isArray(formData.universityCollege)) {
      gradSchools = formData.universityCollege.join('||');
    } else if (typeof formData.universityCollege === 'string') {
      gradSchools = formData.universityCollege;
    } else {
      gradSchools = '';
    }

    let formSubmissionData: IRequestSubmissionData = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phoneNumber: formData.phoneNumber,
      password: formData.password,
      userType: formData.userType,
      isAlumni: formData.isAlumni,
      nodeTypeId: formData.nodeTypeId,
      researchInterests: formData.researchInterests,
      achieveGoal: formData.achieveGoal,
      sendCopy: formData.sendCopy,
      DepartmentId: formData.DepartmentId,
      GraduateId: formData.GraduateId,
    };

    formSubmissionData.middleName = formData.middleName;
    formSubmissionData.maidenName = formData.maidenName;
    formSubmissionData.achieveGoal = formData.achieveGoal;
    formSubmissionData.gradSchools = gradSchools;
    formSubmissionData.deptOfStudy = formData.department;
    formSubmissionData.graduateDegreeProgram = formData.degree;
    formSubmissionData.rotationDate =
      formData.expectedGraduationDate ?? formData.startDate;
    formSubmissionData.citizenship = formData.nationality;
    formSubmissionData.service = formData.organizationService;
    formSubmissionData.rank = formData.rank;
    formSubmissionData.jobTitle = formData.jobDescription;
    formSubmissionData.currentOrganization = formData.organization;

    if (formData.otherService) {
      formSubmissionData.otherService = formData.otherService;
    }
    await sendOtpToUser();
    setDialogOpen(true);
    setFormSubmissionData(formSubmissionData);
  };

  const addUserToDb = async () => {
    setAddingUser(true);
    const usersData: IAddUsers = {
      users: [formSubmissionData],
    };
    await submitFormData(usersData)
      .then((userResponseData) => {
        if (userResponseData && userResponseData.status === 200) {
          setUserSubmitted(true);
          setFirstName(formData.firstName);
          setFormData({
            firstName: '',
            middleName: '',
            lastName: '',
            maidenName: '',
            email: '',
            phoneNumber: '',
            password: '',
            userType: '',
            isAlumni: false,
            nodeTypeId: 0,
            researchInterests: [],
            achieveGoal: '',
            sendCopy: false,
            department: '',
            organization: '',
            course: '',
            otherService: '',
            nationality: '',
            universityCollege: [],
            DepartmentId: 0,
            GraduateId: 0,
          } as IRequestFormData);
        }
      })
      .catch((_error) => {})
      .finally(() => {
        setAddingUser(false);
      });
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setOtp('');
  };

  const handleChange = (newValue: string) => {
    setOtp(newValue);
  };

  const sendOtpToUser = async () => {
    const sendOTPData = {
      email: formData.email,
    };

    await sendOTP(sendOTPData)
      .then((sendOTPResponse) => {
        if (sendOTPResponse && sendOTPResponse.status === 200) {
          setAlertSeverity('success');
          setOtpStatusMessage(
            `OTP sent to ${formData.email}.If not received,please check your SPAM folder before resending.`
          );
        }
      })
      .catch((_error) => {
        setAlertSeverity('error');
        setOtpStatusMessage(`Failed to send OTP to ${formData.email}`);
      });
  };

  const handleResendClick = async () => {
    await sendOtpToUser();
  };

  const handleOTPSubmitClick = async () => {
    const verifyOTPData = {
      email: formData.email,
      otp: otp,
    };

    await verifyOTP(verifyOTPData)
      .then(async (verifyOTPResponse) => {
        if (verifyOTPResponse && verifyOTPResponse.status === 200) {
          setOtpStatusMessage(`Sucessfully verified OTP`);
          setTimeout(() => {}, 3000);
          setOtpVerified(true);
          setAlertSeverity('success');
          addUserToDb();
        }
      })
      .catch((error) => {
        if (error.response) {
          setAlertSeverity('error');
          setOtpStatusMessage(
            error.response.data ?? 'Something went wrong.Please try again!'
          );
        }
      });
  };

  const handleTryAgainButtonClick = () => {
    resetStates();
  };

  const resetStates = () => {
    setUserSubmitted(false);
    setOtpVerified(false);
    setOtpStatusMessage('');
    setFormSubmitted(false);
    setDialogOpen(false);
    setAlertSeverity('info');
    setPasswordError(false);
    setOtp('');
  };

  const welcomeText = () => {
    return (
      <Stack spacing={3}>
        <Typography variant='h5'>{`Hello ${firstName},`}</Typography>
        <Typography variant='h5'>Welcome to Athena!</Typography>
        <Typography variant='h6'>
          You will be receiving an email with login instructions in the next few
          hours. Please be sure to check your SPAM folder so it is not missed!
        </Typography>
        <Typography variant='h6'>
          Thanks,<br></br> The Athena Team
        </Typography>
      </Stack>
    );
  };

  const OTPForm = () => {
    return (
      <Dialog open={dialogOpen}>
        <Box display='flex' alignItems='center'>
          <Box flexGrow={1}>
            {' '}
            <DialogTitle align='center'>Verify OTP</DialogTitle>{' '}
          </Box>
          <Box>
            <IconButton onClick={handleDialogClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
        <DialogContent>
          <Stack spacing={3}>
            <Typography>
              An One-Time-Password (OTP) is sent to your registered email please
              verify to continue.
            </Typography>
            <MuiOtpInput
              value={otp}
              onChange={handleChange}
              length={6}
              autoFocus
            />
            {otpStatusMessage && (
              <Alert severity={alertSeverity}>{otpStatusMessage}</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleResendClick} color='secondary'>
            Resend
          </Button>
          <Button
            onClick={handleOTPSubmitClick}
            color='primary'
            variant='contained'
          >
            Verify
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const addUserDialog = () => {
    return (
      <Dialog
        open={true}
        fullWidth
        sx={{
          minHeight: '600px',
          maxHeight: '600px',
        }}
      >
        <DialogContent sx={{ minHeight: '200px' }}>
          {addingUser ? (
            <Stack alignItems={'center'} sx={{ paddingTop: '70px' }}>
              <CircularProgress color='info' />
              <Typography variant='h6'>Please wait...</Typography>
            </Stack>
          ) : (
            <>
              {' '}
              {userSubmitted ? (
                <Stack spacing={5}>
                  <Typography>{welcomeText()}</Typography>
                </Stack>
              ) : (
                <Stack>
                  <Typography variant='h3'>
                    Sorry, something went wrong. Please try again
                  </Typography>
                </Stack>
              )}{' '}
            </>
          )}
        </DialogContent>
        <DialogActions>
          {!userSubmitted && (
            <Button
              variant='outlined'
              startIcon={<ReplayIcon />}
              onClick={handleTryAgainButtonClick}
            >
              Try again
            </Button>
          )}
          <Button variant='contained' startIcon={<HomeIcon />} href='/'>
            Home
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // const temporaryDialog = () => {
  //   return (
  //     <Dialog
  //       open={true}
  //       fullWidth
  //       sx={{
  //         minHeight: "600px",
  //         maxHeight: "600px",
  //       }}
  //     >
  //       <DialogContent sx={{ minHeight: "200px" }}>
  //         {addingUser ? (
  //           <Stack alignItems={"center"} sx={{ paddingTop: "70px" }}>
  //             <CircularProgress color="info" />
  //             <Typography variant="h6">Please wait...</Typography>
  //           </Stack>
  //         ) : (
  //           <>
  //             {" "}
  //             {userSubmitted ? (
  //               <Stack spacing={5}>
  //                 <Typography>{welcomeText()}</Typography>
  //               </Stack>
  //             ) : (
  //               userSubmissionFailed && (
  //                 <Stack>
  //                   <Typography variant="h3">
  //                     Sorry, something went wrong. Please try again
  //                   </Typography>
  //                 </Stack>
  //               )
  //             )}{" "}
  //           </>
  //         )}
  //       </DialogContent>
  //       <DialogActions>
  //         <Button
  //           variant="contained"
  //           startIcon={<CloseIcon />}
  //           href="/request-account"
  //         >
  //           Close
  //         </Button>
  //       </DialogActions>
  //     </Dialog>
  //   );
  // };

  return (
    <div id='request-form-root'>
      <div className='blue-section'>
        <img
          src={npsfLogoWhite}
          alt='NPSF Logo'
          style={{ width: '150px', height: 'auto', marginBottom: '1rem' }}
        />
        <h1>ATHENA for Partners</h1>
        <h2>Account Request Form</h2>
        <p>
          If you or your company would like to be a part of the problem-solving
          network of the Naval Postgraduate School and defense innovation
          ecosystem, fill out the form below to register your interest and
          request an Athena for Partners account.
        </p>
        <p>
          Athena for Partners extends collaboration beyond the DOD, engaging
          with the innovation ecosystem outside the military to leverage
          external expertise and industry insights for effective solution
          development.
        </p>
        <p>Questions? Email athena@npsfoundation.org</p>
      </div>
      <div className='form-wrapper'>
        <form onSubmit={formSubmission}>
          <TextField
            label='First Name'
            name='firstName'
            fullWidth
            required
            margin='normal'
            variant='outlined'
            helperText='This will be used to create your Athena username in the format of [first name or forename initial][last name or surname].'
            onChange={handleInputChange}
          />
          <TextField
            label='Middle Name'
            name='middleName'
            fullWidth
            margin='normal'
            variant='outlined'
            helperText='This information will be used to search through Athena to match you with any documents you have authored.'
            onChange={handleInputChange}
          />
          <TextField
            label='Last Name or Surname'
            name='lastName'
            fullWidth
            required
            margin='normal'
            variant='outlined'
            helperText='This will be used to create your Athena username in the format of [first name or forename initial][last name or surname].'
            onChange={handleInputChange}
          />
          <TextField
            label="Maiden Name or 'Also Known As' Name"
            name='maidenName'
            fullWidth
            margin='normal'
            variant='outlined'
            helperText='Please list your maiden name or an Also Know As name if you have published a thesis or other documents under a different name. This will allow us to match your published documents to your published name.'
            onChange={handleInputChange}
          />
          <TextField
            label='Email Address'
            name='email'
            type='email'
            required
            fullWidth
            margin='normal'
            variant='outlined'
            helperText='Please use your official organization email for verification purposes.'
            onChange={handleInputChange}
          />
          <TextField
            inputRef={passwordRef}
            label='Password'
            name='password'
            type='password'
            helperText='Password minimum eight characters, at least one uppercase letter, one lowercase letter and one number'
            required
            fullWidth
            autoFocus
            margin='normal'
            variant='outlined'
            error={passwordError}
            onChange={handleInputChange}
          />
          <FormControl autoFocus fullWidth margin='normal' ref={phoneNumberRef}>
            <PhoneInput
              copyNumbersOnly
              country={'us'}
              inputStyle={{ width: '100%' }}
              specialLabel='Phone Number'
              containerStyle={{ marginBottom: '16px' }}
              onChange={handlePhoneNumberChange}
              placeholder='Phone number'
              inputProps={{
                required: false,
              }}
              enableSearch
              isValid={() => {
                if (!formSubmitted) {
                  return true;
                } else {
                  if (isValid) {
                    return true;
                  } else {
                    return 'Enter a valid phone number';
                  }
                }
              }}
            />
          </FormControl>

          <FormControl fullWidth margin='normal'>
            <InputLabel>User Type</InputLabel>
            <Select
              value={formData.userType}
              onChange={handleDropdownChange}
              label='User Type'
              required
            >
              {userTypesData
                .filter((type) => type.UserTypeId !== 0)
                .map((type) => (
                  <MenuItem key={type.NodeTypeId} value={type.Title}>
                    {type.Title}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin='normal' required>
            <InputLabel>
              Are you a current NPS student or an NPS alumni?
            </InputLabel>
            <Select
              value={isAlumni === null ? '' : isAlumni ? 'yes' : 'no'}
              onChange={(event) => {
                const isAffiliated = event.target.value === 'yes';
                setIsAlumni(isAffiliated);
                setFormData({
                  ...formData,
                  isAlumni: isAffiliated,
                });
              }}
              label='Are you a current NPS student or an NPS alumni?'
            >
              <MenuItem value='yes'>Yes</MenuItem>
              <MenuItem value='no'>No</MenuItem>
            </Select>
          </FormControl>
          {renderAdditionalFields()}
          <KeywordSearchDropdown
            getSelectedKeywords={getSelectedKeywords}
            label='Research Interests'
          />
          <TextField
            label='What do you hope to achieve as an Athena user?'
            name='achieveGoal'
            fullWidth
            margin='normal'
            variant='outlined'
            multiline
            onChange={handleInputChange}
          />
          <Button
            type='submit'
            variant='contained'
            color='primary'
            style={{ marginTop: '20px' }}
          >
            Submit
          </Button>
          {!otpVerified ? OTPForm() : addUserDialog()}
        </form>
      </div>
    </div>
  );
};

export default RequestForm;
